import {
  PortfolioCompositionRowDTO,
  PortfolioCompositionSectionDTO,
} from "@models/platform-analysis/entities/steps/summary";
import { SelectedProductDTO } from "@models/platform-analysis/entities/steps/feature";
import { PDFPortfolioCompositionTableConfigs as Configs } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/portfolio-composition/configs";
import {
  PAGE_BODY_HEIGHT_WITHOUT_HEADING,
  SUB_TITLE_HEIGHT,
} from "@pages/platform-analysis/components/buttons/export-pdf/constant";
import {
  PageTemplate,
  PDFFeePortfolioComposition,
} from "@pages/platform-analysis/components/buttons/export-pdf/model";
import { PDFFeePortfolioCompositionSection } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/portfolio-composition/index";
import { chunk } from "lodash";
import { pagesHandler } from "@pages/platform-analysis/components/buttons/export-pdf/util";
import {
  getPortfolioCompositionSelectedProducts,
  getPortfolioCompositionSelectedSections,
} from "@pages/platform-analysis/util";

const getRowFlatMap = ({
  sections,
  startIndex,
  endIndex,
  shouldHideSubTotal,
  hiddenColumnIndexMap = [],
}: {
  sections: PortfolioCompositionSectionDTO[];
  startIndex: number;
  endIndex: number;
  shouldHideSubTotal?: boolean;
  hiddenColumnIndexMap?: boolean[];
}): PortfolioCompositionRowDTO[] => {
  const selectedSections = getPortfolioCompositionSelectedSections({
    sections,
    hiddenColumnIndexMap: hiddenColumnIndexMap,
    startSliceIndex: startIndex,
    endSliceIndex: endIndex,
    shouldHideSubTotal,
  });

  return selectedSections.flatMap((section) => section.rows);
};

const getEstimatedRowHeight = ({
  rowName,
  isSubTotal,
  shouldHideSubTotal,
}: {
  rowName: string;
  isSubTotal: boolean;
  shouldHideSubTotal: boolean;
}): {
  estimatedHeight: number;
  numberOfLines: number;
} => {
  const numberOfLines = Math.ceil(
    rowName.length /
      (isSubTotal || shouldHideSubTotal
        ? Configs.SubTotalMaxCharsPerLine
        : Configs.DefaultMaxCharsPerLine),
  );

  return {
    estimatedHeight:
      Configs.DefaultRowHeight +
      Configs.RowHeightLinesMultiplier * (numberOfLines - 1),
    numberOfLines: numberOfLines,
  };
};

const getSelectedRowsHeight = ({
  startCursorIndex,
  rowFlatMap,
  spaceAvailable = PAGE_BODY_HEIGHT_WITHOUT_HEADING,
  shouldHideSubTotal,
}: {
  startCursorIndex: number;
  rowFlatMap: PortfolioCompositionRowDTO[];
  spaceAvailable?: number;
  shouldHideSubTotal: boolean;
}): {
  sumHeight: number;
  endIndex: number;
  belowMinimumInvestmentSize: boolean;
} => {
  let rowSumHeightLocal = Configs.TableHeaderHeight + Configs.TableTitleHeight;
  let currentCursorIndex = startCursorIndex;
  let belowMinimumInvestmentSize = false;

  while (rowSumHeightLocal < spaceAvailable) {
    const row = rowFlatMap[currentCursorIndex];

    if (!row) break;

    const containBelowInvestment = row.cells?.some(
      (cell) => cell.belowMinimumInvestmentSize,
    );
    if (containBelowInvestment) belowMinimumInvestmentSize = true;

    const rowName = row.name || "";
    const { estimatedHeight, numberOfLines } = getEstimatedRowHeight({
      rowName,
      isSubTotal: Boolean(row.isSubTotal),
      shouldHideSubTotal,
    });
    const isLastRow = currentCursorIndex === rowFlatMap.length - 1;
    const shouldIncludeLastRow = isLastRow && numberOfLines === 1;

    if (
      rowSumHeightLocal + estimatedHeight > spaceAvailable &&
      !shouldIncludeLastRow
    )
      break;

    rowSumHeightLocal += estimatedHeight;
    currentCursorIndex++;
  }

  return {
    sumHeight: rowSumHeightLocal,
    endIndex: currentCursorIndex,
    belowMinimumInvestmentSize,
  };
};

const handleAppendTable = ({
  pages,
  portfolioCompositionData,
  selectedProducts = [],
  startSliceProductIndex,
  endSliceProductIndex,
  spaceAvailable,
  isFollowingTable,
  isLastTable,
  numOfHiddenProducts,
}: {
  pages: PageTemplate[];
  portfolioCompositionData?: PDFFeePortfolioComposition;
  selectedProducts: SelectedProductDTO[];
  numOfHiddenProducts: number;
  startSliceProductIndex: number;
  endSliceProductIndex: number;
  spaceAvailable: number;
  isFollowingTable?: boolean;
  isLastTable?: boolean;
}) => {
  const sections = portfolioCompositionData?.sections ?? [];
  const shouldHideSubTotal =
    !!portfolioCompositionData?.hidePortfolioCompositionSubtotals;
  const hiddenColumnIndexMap = portfolioCompositionData?.hiddenProductsIndexMap;

  const selectedProductsLocal = selectedProducts.slice(
    startSliceProductIndex,
    endSliceProductIndex,
  );

  const rowFlatMap = getRowFlatMap({
    sections,
    startIndex: startSliceProductIndex,
    endIndex: endSliceProductIndex,
    shouldHideSubTotal: shouldHideSubTotal,
    hiddenColumnIndexMap,
  });

  let rowCursorIndex = 0;
  let spaceAvailableAfterAppend = spaceAvailable;

  while (rowCursorIndex < rowFlatMap.length) {
    const startSlice = rowCursorIndex;
    // Determine space available based on whether entire table fits
    const spaceForCalculation = spaceAvailableAfterAppend;
    const { endIndex, belowMinimumInvestmentSize, sumHeight } =
      getSelectedRowsHeight({
        startCursorIndex: rowCursorIndex,
        rowFlatMap,
        spaceAvailable: spaceForCalculation,
        shouldHideSubTotal,
      });

    const isEntireTableFit = endIndex === rowFlatMap.length;

    // If table doesn't fit, recalculate with full page height
    const finalCalculation = isEntireTableFit
      ? { endIndex, belowMinimumInvestmentSize, sumHeight }
      : getSelectedRowsHeight({
          startCursorIndex: rowCursorIndex,
          rowFlatMap,
          spaceAvailable: PAGE_BODY_HEIGHT_WITHOUT_HEADING,
          shouldHideSubTotal,
        });

    const showNumOfHiddenProducts =
      numOfHiddenProducts > 0 &&
      isLastTable &&
      finalCalculation.endIndex === rowFlatMap.length;

    const portfolioCompositionComp = (
      <PDFFeePortfolioCompositionSection
        selectedProducts={selectedProductsLocal}
        numOfProducts={selectedProducts.length}
        rows={rowFlatMap.slice(startSlice, finalCalculation.endIndex)}
        startSliceProductIndex={startSliceProductIndex}
        endSliceProductIndex={endSliceProductIndex}
        belowMinimumInvestmentSize={finalCalculation.belowMinimumInvestmentSize}
        isAppending={isEntireTableFit}
        isFollowingTable={isFollowingTable}
        shouldHideSubTotal={shouldHideSubTotal}
        numOfHiddenProducts={
          showNumOfHiddenProducts ? numOfHiddenProducts : undefined
        }
      />
    );

    pagesHandler(pages, portfolioCompositionComp, isEntireTableFit);

    rowCursorIndex = finalCalculation.endIndex;
    spaceAvailableAfterAppend =
      (isEntireTableFit
        ? spaceAvailableAfterAppend
        : PAGE_BODY_HEIGHT_WITHOUT_HEADING) -
      finalCalculation.sumHeight -
      (finalCalculation.belowMinimumInvestmentSize
        ? Configs.TableFooterHeight
        : 0) -
      Configs.TableTitleHeight;
  }

  return spaceAvailableAfterAppend;
};

const handleAppendNoDataSection = ({
  pages,
  spaceAvailable,
  numOfHiddenProducts,
}: {
  pages: PageTemplate[];
  spaceAvailable: number;
  numOfHiddenProducts: number;
}) => {
  const entireSectionHeight =
    Configs.TableTitleHeight +
    Configs.NoDataNoteHeight +
    Configs.HiddenProductsNoteHeight;
  const shouldAppend = spaceAvailable >= entireSectionHeight;
  const comp = (
    <PDFFeePortfolioCompositionSection
      selectedProducts={[]}
      numOfHiddenProducts={numOfHiddenProducts}
      isAppending={shouldAppend}
    />
  );

  pagesHandler(pages, comp, shouldAppend);

  return shouldAppend
    ? spaceAvailable - Configs.TableTitleHeight - Configs.NoDataNoteHeight
    : PAGE_BODY_HEIGHT_WITHOUT_HEADING -
        SUB_TITLE_HEIGHT -
        Configs.NoDataNoteHeight;
};

export const feeAnalysisPortfolioCompositionPageHandler = ({
  pages,
  spaceAvailable,
  portfolioCompositionData,
}: {
  pages: PageTemplate[];
  spaceAvailable: number;
  portfolioCompositionData?: PDFFeePortfolioComposition;
}) => {
  if (!portfolioCompositionData) return spaceAvailable;

  const selectedProducts = getPortfolioCompositionSelectedProducts({
    setups: portfolioCompositionData.setups,
    hiddenColumnIndexMap: portfolioCompositionData.hiddenProductsIndexMap,
  });
  const numOfSections = chunk(
    selectedProducts,
    Configs.MaxProductsDisplay,
  ).length;
  let spaceAvailableAfterAppend = spaceAvailable;
  const numOfHiddenProducts =
    portfolioCompositionData.hiddenProductsIndexMap?.filter(Boolean)?.length ??
    0;

  if (numOfSections === 0) {
    spaceAvailableAfterAppend = handleAppendNoDataSection({
      pages,
      spaceAvailable: spaceAvailableAfterAppend,
      numOfHiddenProducts,
    });
  }

  Array.from({ length: numOfSections }).forEach((_, i) => {
    const startSlice = i * Configs.MaxProductsDisplay;
    const endSlice = Math.min(
      startSlice + Configs.MaxProductsDisplay,
      selectedProducts.length,
    );

    spaceAvailableAfterAppend = handleAppendTable({
      portfolioCompositionData,
      selectedProducts: selectedProducts,
      pages,
      spaceAvailable: spaceAvailableAfterAppend,
      startSliceProductIndex: startSlice,
      endSliceProductIndex: endSlice,
      isFollowingTable: i > 0,
      isLastTable: i === numOfSections - 1,
      numOfHiddenProducts,
    });
  });

  return (
    spaceAvailableAfterAppend -
    Configs.SectionGapHeight -
    (numOfHiddenProducts > 0 ? Configs.HiddenProductsNoteHeight : 0)
  );
};
